import React from "react";

import moment from "moment";
import classNames from "classnames";
import { formatNumberWithCommas } from "@features/utils/functions";
import { IOrder } from "@market/interfaces/IOrder";
import noPhotoCompany from "@images/no_photo_company.png";
import { EordersTypes } from "@market/enums/OrdersTypes";
import { decimalPlaces } from "@appComponents/CurrencyElement/CurrencyElement";
import { Button } from "@appComponents/Button";
import { MAIN_CURRENCY_LABEL } from "@bundles/consts";
import AsyncImage from "@appComponents/AsyncImage/AsyncImage";
import ChevronDownIcon from "../../../../icons/chevron-down-black.svg";

const typeLabels = {
  [EordersTypes.buy]: "Buy",
  [EordersTypes.sell]: "Sell",
};

type GetMyOrderColumnsArgs = {
  handleOpenLighbox: (orderId: number, orderType: EordersTypes) => void;
  isMobile: boolean;
  handleExpandToggle: (order: IOrder) => void;
  expandedRowKeys: React.Key[];
};

export const getMyOrderColumns = ({
  handleOpenLighbox,
  isMobile,
  handleExpandToggle,
  expandedRowKeys,
}: GetMyOrderColumnsArgs) =>
  isMobile
    ? [
        {
          title: "NAME",
          dataIndex: "spv_name",
          key: "spv_name",
          render: (_, order: IOrder) => {
            return (
              <div className="flex items-center gap-2">
                <AsyncImage
                  image={order.company_avatar?.url}
                  fallback={noPhotoCompany}
                >
                  <img alt="logo" className="h-[36px] w-[36px] rounded-[5px]" />
                </AsyncImage>

                <span>{order.spv_name}</span>
              </div>
            );
          },
        },
        {
          title: "",
          dataIndex: "action",
          key: "action",
          width: 10,
          render: (_, order: IOrder) => {
            return (
              <Button isSmall onClick={() => handleExpandToggle(order)}>
                <span
                  className={classNames({
                    "rotate-180": expandedRowKeys.includes(order.id),
                  })}
                >
                  <ChevronDownIcon />
                </span>
              </Button>
            );
          },
        },
      ]
    : [
        {
          title: "NAME",
          dataIndex: "spv_name",
          key: "spv_name",
          sorter: (a, b) => a.spv_name.localeCompare(b.spv_name),
          render: (_, order: IOrder) => {
            return (
              <div className="flex items-center gap-2">
                <AsyncImage
                  image={order.company_avatar?.url}
                  fallback={noPhotoCompany}
                >
                  <img alt="logo" className="h-[36px] w-[36px] rounded-[5px]" />
                </AsyncImage>

                <span>{order.spv_name}</span>
              </div>
            );
          },
        },
        {
          title: "DATE",
          dataIndex: "created_at",
          key: "created_at",
          sorter: (a, b) =>
            moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
          render: (_, order: IOrder) => {
            return (
              <div>
                <div>{moment(order.created_at).format("DD MMM, YYYY")}</div>
                <div className="text-xs">
                  {moment(order.created_at).format("h:mm A")}
                </div>
              </div>
            );
          },
        },
        {
          title: "TYPE",
          dataIndex: "type",
          key: "type",
          sorter: (a, b) => a.type.localeCompare(b.type),
          render: (_, order: IOrder) => {
            return (
              <div
                className={classNames({
                  "text-[#2D9C5E]": order.type === EordersTypes.buy,
                  "text-[#E02020]": order.type === EordersTypes.sell,
                })}
              >
                {typeLabels[order.type]}
              </div>
            );
          },
        },
        {
          title: "PRICE",
          dataIndex: "price",
          key: "price",
          sorter: (a, b) => Number(a.price ?? 0) - Number(b.price ?? 0),
          render: (_, order: IOrder) => {
            if (!order.price) {
              return "-";
            }

            return (
              <div>
                {`${formatNumberWithCommas(
                  Number(order.price ?? 0),
                  decimalPlaces[order.payment_currency]
                )} ${
                  order.payment_currency?.toUpperCase() ??
                  MAIN_CURRENCY_LABEL.toUpperCase()
                }`}
              </div>
            );
          },
        },
        {
          title: "AMOUNT",
          dataIndex: "number_of_shares",
          key: "number_of_shares",
          sorter: (a, b) =>
            Number(a.number_of_shares ?? 0) - Number(b.number_of_shares ?? 0),
        },
        {
          title: "VALUE",
          dataIndex: "value",
          key: "value",
          sorter: (a, b) => Number(a.value ?? 0) - Number(b.value ?? 0),
          render: (_, order: IOrder) => {
            if (!order.value) {
              return "-";
            }

            return (
              <div>
                {`${formatNumberWithCommas(
                  Number(order.value ?? 0),
                  decimalPlaces[order.payment_currency]
                )} ${
                  order.payment_currency?.toUpperCase() ??
                  MAIN_CURRENCY_LABEL.toUpperCase()
                }`}
              </div>
            );
          },
        },

        {
          title: "",
          dataIndex: "status",
          key: "status",
          render: (_, order: IOrder) => {
            if (order.status === "sold_out") {
              return order.type === EordersTypes.buy
                ? "Bought Out"
                : "Sold Out";
            }

            if (
              order.status !== "suspended" &&
              order.number_of_available_shares > 0
            ) {
              return (
                <Button
                  isSmall
                  onClick={() => handleOpenLighbox(order.id, order.type)}
                  data-cy="cancel"
                >
                  Cancel
                </Button>
              );
            } else {
              return (
                <Button isSmall disabled data-cy="cancel">
                  Cancel
                </Button>
              );
            }
          },
        },
      ];
